// 2D region viewer: streamed COG raster (one active), toggleable vector
// overlays, click-to-inspect lines, legend + scale bar.
import { CogLayer } from "./cog";
import { clear, h } from "./dom";
import { className, fmtLength, layerName, pick, t } from "./i18n";
import { colorbar, fmtMeters, scaleBar } from "./legend";
import type { RegionManifest, VectorData, VectorFeature, VectorLayer } from "./types";
import { Viewport } from "./view";
import { classColor, drawFeatures, hitTest } from "./vectors";

export function renderViewer(root: HTMLElement, slug: string): () => void {
  const isMobile = window.matchMedia("(max-width: 720px)").matches;
  const canvas = h("canvas", { className: "map-canvas" });
  const stage = h("div", { className: "stage" }, canvas);
  const toggleLabel = h("span", {}, t("viewer.layers"));
  const panelToggle = h(
    "button",
    { className: "panel-toggle", onClick: () => { panel.classList.toggle("collapsed"); updateToggle(); } },
    h("span", { className: "panel-toggle-chevron" }, "▴"),
    toggleLabel,
  );
  const panelContent = h("div", { className: "panel-content" }, h("p", { className: "muted" }, t("viewer.loading")));
  const panel = h("aside", { className: "panel" + (isMobile ? " collapsed" : "") }, panelToggle, panelContent);
  const scaleLabel = h("span", { className: "scalebar-label" });
  const scaleLine = h("div", { className: "scalebar-line" });
  const scalebarEl = h("div", { className: "scalebar" }, scaleLabel, scaleLine);
  const chip = h("div", { className: "feature-chip hidden" });
  stage.append(scalebarEl, chip);
  const wrap = h("div", { className: "viewer" }, stage, panel);
  clear(root).append(wrap);

  const viewport = new Viewport(canvas);
  const cogs = new Map<string, CogLayer>();
  const vectors = new Map<string, VectorData>();
  const visibleVectors = new Set<string>();
  let manifest: RegionManifest | null = null;
  let activeRaster = "";
  let opacity = 0.9;
  let hovered: VectorFeature | null = null;
  let selected: VectorFeature | null = null;
  let disposed = false;
  let raf = 0;

  function requestRender(): void {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      render();
    });
  }

  function base(file: string): string {
    return `/data/regions/${slug}/${file}`;
  }

  function activeCog(): CogLayer | null {
    return cogs.get(activeRaster) ?? null;
  }

  function visibleFeatures(): VectorFeature[] {
    const out: VectorFeature[] = [];
    for (const id of visibleVectors) {
      const d = vectors.get(id);
      if (d) out.push(...d.features);
    }
    return out;
  }

  function render(): void {
    if (disposed || !manifest) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = viewport.dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue("--bg");
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const s = viewport.scale * dpr;
    ctx.setTransform(s, 0, 0, s, viewport.tx * dpr, viewport.ty * dpr);
    activeCog()?.draw(ctx, canvas.width, canvas.height);
    drawFeatures(ctx, visibleFeatures(), s, viewport.scale, opacity, hovered ?? selected);
    const [m, px] = scaleBar(manifest.px_size_m, viewport.scale);
    scaleLabel.textContent = fmtMeters(m);
    scaleLine.style.width = `${Math.round(px)}px`;
  }

  function selectRaster(id: string): void {
    if (!manifest) return;
    activeRaster = id;
    if (!cogs.has(id)) {
      const layer = manifest.rasters.find((r) => r.id === id);
      if (layer) cogs.set(id, new CogLayer(base(layer.file), requestRender));
    }
    buildPanel();
    requestRender();
  }

  async function toggleVector(id: string, on: boolean): Promise<void> {
    if (on) {
      visibleVectors.add(id);
      if (!vectors.has(id) && manifest) {
        const layer = manifest.vectors.find((v) => v.id === id);
        if (layer) {
          try {
            const r = await fetch(base(layer.file));
            vectors.set(id, (await r.json()) as VectorData);
          } catch {
            visibleVectors.delete(id);
          }
        }
      }
    } else {
      visibleVectors.delete(id);
      if (selected && !visibleFeatures().includes(selected)) showChip(null, 0, 0);
    }
    buildPanel();
    requestRender();
  }

  function updateToggle(): void {
    const layer = manifest?.rasters.find((r) => r.id === activeRaster);
    toggleLabel.textContent = layer ? `${t("viewer.layers")} · ${layerName(layer.kind)}` : t("viewer.layers");
    const chev = panelToggle.querySelector(".panel-toggle-chevron");
    if (chev) chev.textContent = panel.classList.contains("collapsed") ? "▴" : "▾";
  }

  function buildPanel(): void {
    if (!manifest) return;
    updateToggle();
    const panel_ = panelContent;
    clear(panel_);
    panel_.append(h("h2", {}, pick(manifest.name)));
    const desc = pick(manifest.description);
    if (desc) panel_.append(h("p", { className: "muted" }, desc));

    panel_.append(h("h3", {}, t("viewer.layers")));
    const rasterBox = h("div", { className: "choices" });
    for (const r of manifest.rasters) {
      rasterBox.append(
        h(
          "label",
          { className: "choice" },
          h("input", {
            type: "radio",
            name: "raster",
            checked: r.id === activeRaster,
            onChange: () => selectRaster(r.id),
          }),
          h("span", {}, layerName(r.kind)),
        ),
      );
    }
    panel_.append(rasterBox);

    if (manifest.vectors.length) {
      panel_.append(h("h3", {}, t("viewer.overlays")));
      const vecBox = h("div", { className: "choices" });
      for (const v of manifest.vectors) {
        vecBox.append(
          h(
            "label",
            { className: "choice" },
            h("input", {
              type: "checkbox",
              checked: visibleVectors.has(v.id),
              onChange: (e: Event) => void toggleVector(v.id, (e.target as HTMLInputElement).checked),
            }),
            h("span", { className: "classdot", style: { background: classColor(v.kind) } }),
            h("span", {}, className(v.kind)),
          ),
        );
      }
      vecBox.append(
        h(
          "label",
          { className: "choice slider" },
          h("span", {}, t("viewer.opacity")),
          h("input", {
            type: "range",
            min: "20",
            max: "100",
            value: String(Math.round(opacity * 100)),
            onInput: (e: Event) => {
              opacity = Number((e.target as HTMLInputElement).value) / 100;
              requestRender();
            },
          }),
        ),
      );
      panel_.append(vecBox);
    }

    const spec = manifest.rasters.find((r) => r.id === activeRaster)?.render;
    const bar = spec ? colorbar(spec) : null;
    if (bar) panel_.append(h("h3", {}, t("viewer.legend")), bar);

    panel_.append(
      h("div", { className: "panel-actions" },
        h("button", { className: "btn", onClick: () => { if (manifest) { viewport.fit(manifest.width, manifest.height); requestRender(); } } }, t("viewer.fit"))),
      h("p", { className: "muted hint" }, t("viewer.click_hint")),
    );
    if (manifest.attribution) panel_.append(h("p", { className: "muted attribution" }, pick(manifest.attribution)));
  }

  function showChip(f: VectorFeature | null, sx: number, sy: number): void {
    selected = f;
    if (!f) {
      chip.classList.add("hidden");
      requestRender();
      return;
    }
    clear(chip).append(
      h("span", { className: "classdot", style: { background: classColor(f.class) } }),
      h("strong", {}, className(f.class)),
      h("span", {}, ` · ${t("chip.length")}: ${fmtLength(f.length_m)}`),
    );
    chip.classList.remove("hidden");
    // anchor centered above the tap/click point, clear of the finger
    const r = stage.getBoundingClientRect();
    chip.style.left = `${Math.min(Math.max(90, sx), r.width - 90)}px`;
    chip.style.top = `${Math.max(8, sy - 72)}px`;
    requestRender();
  }

  // ---------- interactions ----------
  const pointers = new Map<number, { sx: number; sy: number }>();
  let pinch: { dist: number; mx: number; my: number } | null = null;
  let down: { sx: number; sy: number } | null = null;
  let moved = false;

  function pos(e: PointerEvent): { sx: number; sy: number } {
    const r = canvas.getBoundingClientRect();
    return { sx: e.clientX - r.left, sy: e.clientY - r.top };
  }

  function onPointerDown(e: PointerEvent): void {
    canvas.setPointerCapture(e.pointerId);
    const p = pos(e);
    pointers.set(e.pointerId, p);
    if (pointers.size === 2) {
      const [p1, p2] = [...pointers.values()];
      pinch = { dist: Math.hypot(p1.sx - p2.sx, p1.sy - p2.sy), mx: (p1.sx + p2.sx) / 2, my: (p1.sy + p2.sy) / 2 };
      down = null;
      return;
    }
    down = p;
    moved = false;
  }

  function onPointerMove(e: PointerEvent): void {
    const p = pos(e);
    const prev = pointers.get(e.pointerId);
    if (prev) pointers.set(e.pointerId, p);
    if (pinch && pointers.size === 2) {
      const [p1, p2] = [...pointers.values()];
      const dist = Math.hypot(p1.sx - p2.sx, p1.sy - p2.sy);
      const mx = (p1.sx + p2.sx) / 2;
      const my = (p1.sy + p2.sy) / 2;
      if (selected) showChip(null, 0, 0);
      if (pinch.dist > 0 && dist > 0) viewport.zoomAt(mx, my, dist / pinch.dist);
      viewport.panBy(mx - pinch.mx, my - pinch.my);
      pinch = { dist, mx, my };
      requestRender();
      return;
    }
    if (prev && down && e.buttons !== 0) {
      if (Math.hypot(p.sx - down.sx, p.sy - down.sy) > 4) moved = true;
      if (moved && selected) showChip(null, 0, 0); // the chip points at map coords; moving the map orphans it
      viewport.panBy(p.sx - prev.sx, p.sy - prev.sy);
      requestRender();
      return;
    }
    // hover highlight
    const [ix, iy] = viewport.toImage(p.sx, p.sy);
    const feats = visibleFeatures();
    const idx = hitTest(feats, ix, iy, 10 / viewport.scale);
    const next = idx === null ? null : feats[idx];
    if (next !== hovered) {
      hovered = next;
      canvas.style.cursor = hovered ? "pointer" : "grab";
      requestRender();
    }
  }

  function onPointerUp(e: PointerEvent): void {
    const p = pos(e);
    pointers.delete(e.pointerId);
    if (pinch) {
      if (pointers.size < 2) pinch = null;
      return;
    }
    if (down && !moved && e.button === 0) {
      const [ix, iy] = viewport.toImage(p.sx, p.sy);
      const feats = visibleFeatures();
      const idx = hitTest(feats, ix, iy, 10 / viewport.scale);
      showChip(idx === null ? null : feats[idx], p.sx, p.sy);
    }
    down = null;
  }

  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    if (selected) showChip(null, 0, 0);
    const r = canvas.getBoundingClientRect();
    viewport.zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY / 400));
    requestRender();
  }

  // Track the stage, not just the window: unfolding the panel on phones
  // shrinks the stage, and a canvas left at its old pixel size would be
  // squeezed into the smaller box. Render in the same frame — resizing clears
  // the canvas.
  const resizeObserver = new ResizeObserver(() => {
    viewport.resize();
    render();
  });
  resizeObserver.observe(stage);

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerUp);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());

  // ---------- load ----------
  void (async () => {
    try {
      const r = await fetch(`/data/regions/${slug}/region.json`);
      if (!r.ok) throw new Error(String(r.status));
      manifest = (await r.json()) as RegionManifest;
    } catch {
      clear(panelContent).append(h("p", { className: "error" }, t("error.load")));
      return;
    }
    if (disposed) return;
    viewport.resize();
    // space-filling first view on every device; the Fit button shows the whole tile
    viewport.fitCover(manifest.width, manifest.height);
    const def = manifest.rasters.find((x) => x.default) ?? manifest.rasters[0];
    if (def) selectRaster(def.id);
    for (const v of manifest.vectors) void toggleVector(v.id, true);
  })();

  return () => {
    disposed = true;
    if (raf) cancelAnimationFrame(raf);
    resizeObserver.disconnect();
  };
}

export type { VectorLayer };
